import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, X, CheckCircle, Eye, PencilSimple } from "@phosphor-icons/react";

const ROLES = ["jugador", "profesor", "directivo"];
const LABELS = {
  retas: "Retas", torneos: "Torneos", marcadores: "Marcadores", ligas: "Ligas",
  clinicas: "Clínicas", comunidad: "Comunidad", chat: "Chat", tienda: "Tienda",
  balonpie: "Balonpié", mapa: "Mapa mundial",
};
const LVL_STYLE = {
  none: { icon: X, cls: "bg-red-500/10 border-red-500/25 text-red-300", label: "Oculto" },
  read: { icon: Eye, cls: "bg-amber-500/10 border-amber-500/25 text-amber-300", label: "Ver" },
  edit: { icon: PencilSimple, cls: "bg-[#CCFF00]/10 border-[#CCFF00]/30 text-[#CCFF00]", label: "Editar" },
};

export default function AdminVisibility() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin") { navigate("/dashboard"); return; }
    api.get("/admin/visibility").then(({ data }) => setData(data)).catch((e) => toast.error(e.response?.data?.detail || "Error"));
  }, [user, navigate]);

  const setLevel = (section, role, lvl) => {
    setData((d) => ({ ...d, matrix: { ...d.matrix, [section]: { ...d.matrix[section], [role]: lvl } } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/visibility", { matrix: data.matrix });
      toast.success("Permisos guardados");
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-visibility-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] font-bold flex items-center gap-1"><ShieldCheck size={12} weight="fill" /> Admin · Permisos</p>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-black uppercase tracking-tighter">Visibilidad por sección</h1>
          <p className="text-xs text-gray-500 mt-1">Controla qué apartados de la plataforma ve cada rol. Admin siempre ve todo.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs h-9" data-testid="admin-visibility-save">
          <CheckCircle size={14} weight="fill" className="mr-1" /> {saving ? "Guardando…" : "Guardar cambios"}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span className="text-gray-500 uppercase tracking-widest">Niveles:</span>
        {Object.entries(LVL_STYLE).map(([lvl, s]) => {
          const Icon = s.icon;
          return (
            <Badge key={lvl} variant="outline" className={`h-6 uppercase tracking-widest ${s.cls}`}><Icon size={11} className="mr-1" /> {s.label}</Badge>
          );
        })}
      </div>

      <div className="card-tactical p-4 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-gray-500 border-b border-white/10">
              <th className="text-left py-2 pr-4">Sección</th>
              {ROLES.map((r) => <th key={r} className="text-center px-2 pb-2">{r}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.sections.map((s) => (
              <tr key={s} className="border-b border-white/5" data-testid={`vis-row-${s}`}>
                <td className="py-3 pr-4 text-white font-semibold uppercase tracking-widest text-[11px]">{LABELS[s] || s}</td>
                {ROLES.map((role) => {
                  const cur = data.matrix[s]?.[role] || "edit";
                  return (
                    <td key={role} className="px-2 py-2 text-center">
                      <div className="inline-flex border border-white/10 rounded overflow-hidden" data-testid={`vis-cell-${s}-${role}`}>
                        {["none", "read", "edit"].map((lvl) => {
                          const s2 = LVL_STYLE[lvl];
                          const on = cur === lvl;
                          const Icon = s2.icon;
                          return (
                            <button key={lvl} type="button" onClick={() => setLevel(s, role, lvl)}
                              className={`px-2.5 py-1.5 flex items-center gap-1 text-[10px] uppercase tracking-widest transition-colors ${on ? s2.cls : "bg-black/40 text-gray-500 hover:text-gray-300"}`}
                              data-testid={`vis-btn-${s}-${role}-${lvl}`}
                            >
                              <Icon size={11} />{s2.label}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-gray-500 leading-relaxed border-t border-white/5 pt-4">
        <ShieldCheck size={12} className="inline text-[#CCFF00] mr-1" />
        "Oculto" retira la sección del sidebar de ese rol. "Ver" muestra en modo lectura. "Editar" permite crear y modificar.
      </p>
    </div>
  );
}
